"use client";

import { useEffect, useState } from "react";
import { InviteCode, UserBasic } from "@/types";
import { createInviteCode, deactivateInviteCode, fetchMyInviteCodes, fetchCurrentUser, fetchUsersByInviteCode, getUserInfo, updateUserRole } from "@/utils/storage";
import { Copy, Plus, ShieldCheck, Trash2, ChevronDown, ChevronUp, Share } from "lucide-react";

interface InviteProps {
  onBack?: () => void;
}

export default function Invite({ onBack }: InviteProps) {
  const [user, setUser] = useState<UserBasic | null>(getUserInfo());
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [usageLimit, setUsageLimit] = useState<number>(1);
  const [expiryDays, setExpiryDays] = useState<number | "">("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [inviteUsers, setInviteUsers] = useState<Record<string, { open: boolean; loading: boolean; users: UserBasic[]; error?: string }>>({});

  useEffect(() => {
    const init = async () => {
      const u = await fetchCurrentUser();
      setUser(u);
      const list = await fetchMyInviteCodes();
      // Sort by createdAt descending (fresh on top)
      list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      setCodes(list);
    };
    init();
  }, []);

  const handleCreate = async () => {
    if (!user || (user.role !== "superadmin" && user.role !== "admin")) {
      setError("Only superadmin/admin can create invite codes");
      return;
    }
    setError("");
    setLoading(true);
    const code = await createInviteCode(usageLimit, typeof expiryDays === "number" ? expiryDays : undefined);
    if (code) {
      setCodes([code, ...codes]);
    } else {
      setError("Failed to create invite code");
    }
    setLoading(false);
  };

  const handleDeactivate = async (id?: string) => {
    if (!id) return;
    setLoading(true);
    const ok = await deactivateInviteCode(id);
    if (ok) {
      setCodes(codes.map(c => c.id === id ? { ...c, isActive: false } : c));
    } else {
      setError("Failed to deactivate invite code");
    }
    setLoading(false);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Invite code copied to clipboard");
    } catch { }
  };

  const handleShare = async (code: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join LongevityOS',
          text: `I’m using LongevityOS to track and improve long-term health.\nJoin using my invite code: ${code}\nhttps://longevityos.app/`
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      copyToClipboard(`I’m using LongevityOS to track and improve long-term health.\nJoin using my invite code: ${code}\nhttps://longevityos.app/`);
    }
  };

  const toggleInviteUsers = async (inviteId?: string, usageCount?: number) => {
    if (!inviteId || !usageCount || usageCount <= 0) return;

    setInviteUsers((prev) => {
      const current = prev[inviteId];
      if (current?.open) {
        return { ...prev, [inviteId]: { ...current, open: false } };
      }
      return {
        ...prev,
        [inviteId]: {
          open: true,
          loading: !current?.users?.length,
          users: current?.users || [],
          error: "",
        },
      };
    });

    const current = inviteUsers[inviteId];
    if (current?.users?.length) return;

    try {
      const users = await fetchUsersByInviteCode(inviteId);
      setInviteUsers((prev) => ({
        ...prev,
        [inviteId]: { open: true, loading: false, users, error: "" },
      }));
    } catch {
      setInviteUsers((prev) => ({
        ...prev,
        [inviteId]: { open: true, loading: false, users: [], error: "Failed to load users" },
      }));
    }
  };

  const handlePromote = async (userId: string, inviteId: string) => {
    if (!user || user.role !== "superadmin") return;
    const updated = await updateUserRole(userId, "admin");
    if (!updated) {
      alert("Failed to promote user");
      return;
    }
    setInviteUsers((prev) => ({
      ...prev,
      [inviteId]: {
        ...prev[inviteId],
        users: prev[inviteId]?.users?.map((u) => (u.id === userId ? { ...u, role: updated.role } : u)) || [],
      },
    }));
  };

  const isPrivileged = user && (user.role === "superadmin" || user.role === "admin");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-primary" />
          <h2 className="text-sm font-semibold">Invite Others</h2>
          {isPrivileged && <span className="text-[10px] text-text-muted">(Superadmin/Admin)</span>}
        </div>
        {onBack && (
          <button onClick={onBack} className="text-[10px] text-text-muted hover:text-white">Back</button>
        )}
      </div>

      {isPrivileged ? (
        <div className="glass-card p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-text-muted">Usage Limit</label>
              <input
                type="number"
                min={1}
                value={usageLimit}
                onChange={(e) => setUsageLimit(parseInt(e.target.value || "1", 10))}
                className="input-field py-2"
              />
            </div>
            <div>
              <label className="text-[10px] text-text-muted">Expiry (days)</label>
              <input
                type="number"
                min={1}
                value={expiryDays as number | undefined}
                onChange={(e) => setExpiryDays(e.target.value ? parseInt(e.target.value, 10) : "")}
                className="input-field py-2"
              />
            </div>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            onClick={handleCreate}
            disabled={loading}
            className="primary-btn w-full flex items-center justify-center gap-2"
          >
            <Plus size={16} /> {loading ? "Creating..." : "Create Invite Code"}
          </button>
        </div>
      ) : (
        <p className="text-xs text-text-muted">You do not have permission to create invite codes.</p>
      )}

      <div className="space-y-2">
        <h3 className="text-[11px] text-text-muted uppercase tracking-wider">My Codes</h3>
        <div className="space-y-2">
          {codes.length === 0 ? (
            <div className="text-xs text-text-muted">No invite codes yet.</div>
          ) : (
            codes.map((c) => (
              <div key={(c.id || c.code)} className="space-y-2">
                <div className="glass flex items-center justify-between p-3 rounded-xl border border-white/10">
                  <div className="space-y-1">
                    <div className="text-sm font-medium">{c.code}</div>
                    <div className="text-[10px] text-text-muted flex items-center gap-2">
                      <span>Limit {c.usageLimit}</span>
                      <span>•</span>
                      {c.expiresAt && (
                        <>
                          <span className={new Date(c.expiresAt) < new Date() ? "text-red-400" : ""}>
                            {new Date(c.expiresAt) < new Date() ? "Expired" : `Expires ${new Date(c.expiresAt).toLocaleDateString()}`}
                          </span>
                          <span>•</span>
                        </>
                      )}
                      <button
                        className={`underline-offset-2 ${(c.usageCount ?? 0) > 0 ? "hover:underline text-text" : "text-text-muted cursor-default"}`}
                        onClick={() => toggleInviteUsers(c.id || c.code, c.usageCount)}
                        disabled={(c.usageCount ?? 0) === 0}
                      >
                        Used {c.usageCount ?? 0}
                      </button>
                      <span>•</span>
                      <span>{c.isActive === false ? "Inactive" : "Active"}</span>
                      {(c.usageCount ?? 0) > 0 && (
                        inviteUsers[c.id || c.code]?.open ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleShare(c.code)} className="icon-btn text-text-muted hover:text-white" title="Share"><Share size={14} /></button>
                    <button onClick={() => copyToClipboard(c.code)} className="icon-btn text-text-muted hover:text-white" title="Copy"><Copy size={14} /></button>
                    <button onClick={() => handleDeactivate(c.id)} disabled={c.isActive === false} className="icon-btn text-red-400 hover:text-red-300" title="Deactivate"><Trash2 size={14} /></button>
                  </div>
                </div>
                {inviteUsers[c.id || c.code]?.open && (
                  <div className="space-y-2 rounded-lg border border-white/10 bg-black/30 p-3">
                    {inviteUsers[c.id || c.code]?.loading && (
                      <p className="text-[10px] text-text-muted">Loading users...</p>
                    )}
                    {!inviteUsers[c.id || c.code]?.loading && inviteUsers[c.id || c.code]?.error && (
                      <p className="text-[10px] text-red-400">{inviteUsers[c.id || c.code]?.error}</p>
                    )}
                    {!inviteUsers[c.id || c.code]?.loading && !inviteUsers[c.id || c.code]?.error && (inviteUsers[c.id || c.code]?.users?.length ? (
                      inviteUsers[c.id || c.code]?.users.map((u) => (
                        <div key={u.id} className="flex items-center justify-between text-xs">
                          <div>
                            <div className="font-semibold">{u.phoneNumber}</div>
                            <div className="text-[10px] text-text-muted">{u.name || "No name"} • {u.role}</div>
                          </div>
                          {user?.role === "superadmin" && u.role === "user" && (
                            <button
                              onClick={() => handlePromote(u.id, c.id || c.code)}
                              className="text-[10px] text-primary hover:text-white"
                            >
                              Make admin
                            </button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-[10px] text-text-muted">No users created with this code yet.</p>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}